import React, { useState } from 'react';
import { X } from 'lucide-react';
import { useEstates } from '../../lib/useEstates';
import { useUpload } from '../../lib/useUpload';
import type { ReportType } from '../../types';
import { Modal, Alert, UploadProgress, inputClass, labelClass, primaryBtn, secondaryBtn } from '../../components/ui';

export const MANAGER_REPORT_TYPES: ReportType[] = [
  'monthly', 'diesel', 'ekedc', 'staff_report', 'residents_report', 'monthly_kpi', 'security', 'maintenance', 'residents_complaint',
];

export const MANAGER_TYPE_LABELS: Record<string, string> = {
  monthly: 'Monthly Report',
  diesel: 'Diesel Report',
  ekedc: 'EKEDC Units',
  staff_report: 'Staff Report',
  residents_report: 'Residents Report',
  monthly_kpi: 'KPI',
  security: 'Security',
  maintenance: 'Maintenance',
  residents_complaint: "Residents' Complaint",
};

interface ResidentEntry {
  name: string;
  unit: string;
}

interface FormState {
  facility: string;
  report_type: ReportType;
  report_date: string;
  heading: string;
  content: string;
  diesel_used_month: string;
  diesel_liters_remaining: string;
  ekedc_used_month: string;
  severity: string;
  security_location: string;
  action_taken: string;
  resident_name: string;
  unit: string;
  block: string;
  common_area: string;
  residents: ResidentEntry[];
}

export default function ManagerReportFormModal({
  open,
  onClose,
  onSubmitted,
}: {
  open: boolean;
  onClose: () => void;
  onSubmitted: () => void;
}) {
  const { estates, getBlocksForEstate } = useEstates();
  const empty: FormState = {
    facility: '',
    report_type: 'diesel',
    report_date: new Date().toISOString().slice(0, 10),
    heading: '',
    content: '',
    diesel_used_month: '',
    diesel_liters_remaining: '',
    ekedc_used_month: '',
    severity: '',
    security_location: '',
    action_taken: '',
    resident_name: '',
    unit: '',
    block: '',
    common_area: '',
    residents: [{ name: '', unit: '' }],
  };
  const [form, setForm] = useState<FormState>(empty);
  const [files, setFiles] = useState<File[]>([]);
  const upload = useUpload();
  const [error, setError] = useState('');
  const blocks = form.facility ? getBlocksForEstate(form.facility) : [];

  const set = (patch: Partial<FormState>) => setForm((f) => ({ ...f, ...patch }));
  const type = form.report_type;
  const isDiesel = type === 'diesel';
  const isEkedc = type === 'ekedc';
  const isSecurity = type === 'security';
  const isResidents = type === 'residents_report';
  const isResidentsComplaint = type === 'residents_complaint';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    upload.reset();
    setError('');
    try {
      const fd = new FormData();
      fd.append('report_type', form.report_type);
      fd.append('facility', form.facility);
      if (form.heading) fd.append('heading', form.heading);
      fd.append('content', form.content);
      fd.append('report_date', form.report_date);
      if (isDiesel) {
        fd.append('diesel_used_month', form.diesel_used_month);
        if (form.diesel_liters_remaining) fd.append('diesel_liters_remaining', form.diesel_liters_remaining);
      }
      if (isEkedc) {
        fd.append('ekedc_used_month', form.ekedc_used_month);
        if (form.block) fd.append('block', form.block);
        if (form.common_area) fd.append('common_area', form.common_area);
      }
      if (isResidents) {
        if (form.resident_name) fd.append('resident_name', form.resident_name);
        if (form.unit) fd.append('unit', form.unit);
      }
      if (isSecurity) {
        fd.append('severity', form.severity);
        if (form.security_location) fd.append('security_location', form.security_location);
        if (form.action_taken) fd.append('action_taken', form.action_taken);
      }
      if (isResidentsComplaint) {
        const validResidents = form.residents.filter((r) => r.name.trim());
        fd.append('resident_name', JSON.stringify(validResidents));
      }
      files.forEach((f) => fd.append('attachments', f));
      await upload.run('/reports', fd);
      onSubmitted();
      onClose();
      setForm(empty);
      setFiles([]);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Submit Site Report">
      {error && <Alert>{error}</Alert>}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Facility / Estate *</label>
            <select className={inputClass} required value={form.facility} onChange={(e) => set({ facility: e.target.value })}>
              <option value="">Select facility...</option>
              {estates.map((f) => <option key={f.name} value={f.name}>{f.name}</option>)}
            </select>
          </div>
          <div>
            <label className={labelClass}>Report Type *</label>
            <select className={inputClass} required value={form.report_type} onChange={(e) => set({ report_type: e.target.value as ReportType })}>
              {MANAGER_REPORT_TYPES.map((t) => <option key={t} value={t}>{MANAGER_TYPE_LABELS[t]}</option>)}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Report Date</label>
            <input type="date" className={inputClass} value={form.report_date} onChange={(e) => set({ report_date: e.target.value })} />
          </div>
          <div>
            <label className={labelClass}>Report Heading</label>
            <input className={inputClass} value={form.heading} onChange={(e) => set({ heading: e.target.value })} placeholder="e.g. April diesel consumption" />
          </div>
        </div>

        {isDiesel && (
          <div>
            <label className={labelClass}>Total Diesel Used This Month (Liters) *</label>
            <input
              type="number" step="0.01" min="0" required className={inputClass}
              value={form.diesel_used_month}
              onChange={(e) => set({ diesel_used_month: e.target.value })}
              placeholder="e.g. 2400"
            />
            <p className="text-xs text-gray-400 mt-1">Total liters of diesel consumed by the facility this month.</p>
          </div>
        )}

        {isDiesel && (
          <div>
            <label className={labelClass}>Diesel Liters Remaining</label>
            <input
              type="number" step="0.01" min="0" className={inputClass}
              value={form.diesel_liters_remaining}
              onChange={(e) => set({ diesel_liters_remaining: e.target.value })}
              placeholder="e.g. 820"
            />
            <p className="text-xs text-gray-400 mt-1">Current diesel level remaining in the tank, in liters.</p>
          </div>
        )}

        {isEkedc && (
          <>
            <div>
              <label className={labelClass}>Total EKEDC Units Used This Month (kWh) *</label>
              <input
                type="number" step="0.01" min="0" required className={inputClass}
                value={form.ekedc_used_month}
                onChange={(e) => set({ ekedc_used_month: e.target.value })}
                placeholder="e.g. 12500"
              />
              <p className="text-xs text-gray-400 mt-1">Total prepaid electricity units consumed by the facility this month.</p>
            </div>
            {blocks.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Block</label>
                  <select className={inputClass} value={form.block} onChange={(e) => set({ block: e.target.value })}>
                    <option value="">Select block...</option>
                    {blocks.map((b) => <option key={b} value={`Block ${b}`}>Block {b}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Common Area</label>
                  <select className={inputClass} value={form.common_area} onChange={(e) => set({ common_area: e.target.value })}>
                    <option value="">Select...</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                </div>
              </div>
            )}
          </>
        )}

        {isResidents && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Resident Name</label>
              <input className={inputClass} value={form.resident_name} onChange={(e) => set({ resident_name: e.target.value })} placeholder="Optional" />
            </div>
            <div>
              <label className={labelClass}>Unit / Block</label>
              <input className={inputClass} value={form.unit} onChange={(e) => set({ unit: e.target.value })} placeholder="e.g. Block B, Unit 12" />
            </div>
          </div>
        )}

        {isSecurity && (
          <>
            <div>
              <label className={labelClass}>Severity *</label>
              <select className={inputClass} required value={form.severity} onChange={(e) => set({ severity: e.target.value })}>
                <option value="">Select severity...</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Location</label>
              <input className={inputClass} value={form.security_location} onChange={(e) => set({ security_location: e.target.value })} placeholder="Where it occurred" />
            </div>
            <div>
              <label className={labelClass}>Action Taken</label>
              <textarea className={inputClass} rows={3} value={form.action_taken} onChange={(e) => set({ action_taken: e.target.value })} placeholder="What was done to contain or resolve it" />
            </div>
          </>
        )}

        {isResidentsComplaint && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className={labelClass}>Residents & Apartment Numbers *</label>
              {form.residents.length < 10 && (
                <button
                  type="button"
                  onClick={() => set({ residents: [...form.residents, { name: '', unit: '' }] })}
                  className="text-xs text-primary hover:underline"
                >
                  + Add Resident
                </button>
              )}
            </div>
            {form.residents.map((r, idx) => (
              <div key={idx} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2 items-end">
                <div>
                  <input
                    className={inputClass}
                    required={idx === 0}
                    value={r.name}
                    onChange={(e) => {
                      const updated = [...form.residents];
                      updated[idx] = { ...updated[idx], name: e.target.value };
                      set({ residents: updated });
                    }}
                    placeholder="Resident name"
                  />
                </div>
                <div>
                  <input
                    className={inputClass}
                    required={idx === 0}
                    value={r.unit}
                    onChange={(e) => {
                      const updated = [...form.residents];
                      updated[idx] = { ...updated[idx], unit: e.target.value };
                      set({ residents: updated });
                    }}
                    placeholder="Apartment number"
                  />
                </div>
                {form.residents.length > 1 && (
                  <button
                    type="button"
                    onClick={() => set({ residents: form.residents.filter((_, i) => i !== idx) })}
                    className="p-2 text-red-400 hover:text-red-600"
                    title="Remove"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {isDiesel && (
          <p className="text-sm text-gray-400 -mt-2">Enter diesel consumption notes, vendor, cost or delivery details below.</p>
        )}
        {isEkedc && (
          <p className="text-sm text-gray-400 -mt-2">Enter power supply notes, outages or cost details below.</p>
        )}
        {type === 'staff_report' && (
          <p className="text-sm text-gray-400 -mt-2">Summarise staff performance, attendance and coverage for the month.</p>
        )}
        {isResidents && (
          <p className="text-sm text-gray-400 -mt-2">Summarise residents&apos; issues, requests and resolutions for the month.</p>
        )}
        {isResidentsComplaint && (
          <p className="text-sm text-gray-400 -mt-2">Describe the resident complaint. Add all affected residents above.</p>
        )}
        {type === 'monthly_kpi' && (
          <p className="text-sm text-gray-400 -mt-2">Enter your KPI figures and performance notes for the month.</p>
        )}
        {type === 'maintenance' && (
          <p className="text-sm text-gray-400 -mt-2">Summarise maintenance works, repairs and outstanding items for the month.</p>
        )}

        <div>
          <label className={labelClass}>{isSecurity ? 'Security Details *' : isResidentsComplaint ? 'Complaint Details *' : 'Report Details *'}</label>
          <textarea className={inputClass} rows={isSecurity ? 4 : 7} required value={form.content} onChange={(e) => set({ content: e.target.value })} placeholder="Describe the report..." />
        </div>

        <div>
          <label className={labelClass}>Attachments (images, videos, PDFs, Excel, Word)</label>
          <input
            type="file"
            multiple
            accept="image/jpeg,image/png,image/gif,image/webp,video/*,.pdf,.doc,.docx,.xls,.xlsx,.csv"
            className={inputClass}
            onChange={(e) => setFiles(Array.from(e.target.files || []))}
          />
          {files.length > 0 && (
            <p className="text-xs text-gray-500 mt-1">{files.length} file{files.length === 1 ? '' : 's'} selected</p>
          )}
        </div>

        <UploadProgress
          percent={upload.percent}
          phase={upload.phase}
          error={upload.error}
          onCancel={upload.cancel}
        />

        <div className="flex justify-end space-x-3">
          <button type="button" onClick={onClose} className={secondaryBtn}>Cancel</button>
          <button type="submit" disabled={upload.busy} className={primaryBtn}>
            {upload.phase === 'uploading'
              ? `Uploading ${upload.percent}%`
              : upload.phase === 'processing' ? 'Submitting...' : 'Submit Report'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
